import { useQueryClient } from "@tanstack/react-query";
import { BarChart3, Boxes, ClipboardCheck, CreditCard, KeyRound, LayoutDashboard, LogOut, Menu, MessageCircle, PackageOpen, Settings, Sparkles, Star, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { io } from "socket.io-client";
import { useAuth } from "../state/auth";
import { AdminHelpWidget } from "./AdminHelpWidget";
import { Seo } from "./Seo";

const socketUrl = (import.meta.env.VITE_API_URL ?? "http://localhost:5000/api").replace(/\/api$/, "");

export function ProtectedAdmin() {
  const auth = useAuth();
  const { accessToken, refresh } = auth;
  const [checking, setChecking] = useState(!auth.accessToken);
  useEffect(() => {
    if (!accessToken) refresh().finally(() => setChecking(false));
    else setChecking(false);
  }, [accessToken, refresh]);
  if (checking) return <div className="grid min-h-screen place-items-center bg-ink text-cream"><Sparkles className="animate-pulse" /></div>;
  if (!auth.accessToken) return <Navigate to="/admin/login" replace />;
  return <Outlet />;
}

export function AdminLayout() {
  const [open, setOpen] = useState(false);
  const auth = useAuth(); const queryClient = useQueryClient(); const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!auth.accessToken) return;
    const socket = io(socketUrl, { auth: { token: auth.accessToken } });
    ["payment:submitted","payment:reviewed","queue:updated","order:status","help:message","review:submitted"].forEach((event) => socket.on(event, () => {
      queryClient.invalidateQueries({ queryKey: ["admin"] });
    }));
    return () => { socket.disconnect(); };
  }, [auth.accessToken, queryClient]);
  const links = [
    ["/admin", "Overview", LayoutDashboard],
    ["/admin/analytics", "Orders & sales", BarChart3],
    ["/admin/payments", "Payment inbox", ClipboardCheck],
    ["/admin/help-messages", "Help inbox", MessageCircle],
    ["/admin/reviews", "Reviews", Star],
    ["/admin/queue", "Production queue", Boxes],
    ["/admin/products", "Products", PackageOpen],
    ["/admin/payment-methods", "Payment methods", CreditCard],
    ["/admin/settings", "Website content", Settings],
    ["/admin/account", "My account", KeyRound]
  ] as const;
  return <div className="min-h-screen bg-[#F7F4EE]">
    <Seo title="Studio admin" path="/admin" noindex />
    <header className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-ink/10 bg-white px-4 lg:hidden"><button onClick={() => setOpen(true)} aria-label="Open admin menu"><Menu /></button><p className="font-display text-lg font-bold">Minis studio</p><span className="size-6" /></header>
    {open && <button aria-label="Close menu backdrop" onClick={() => setOpen(false)} className="fixed inset-0 z-40 bg-ink/40 lg:hidden" />}
    <aside className={`fixed inset-y-0 left-0 z-50 flex w-72 flex-col bg-ink p-5 text-cream transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
      <div className="flex items-center justify-between"><div className="flex items-center gap-3"><span className="grid size-10 place-items-center rounded-2xl bg-clay"><Sparkles size={20} /></span><div><p className="font-display text-xl font-bold">Minis studio</p><p className="text-xs text-cream/50">Order workspace</p></div></div><button onClick={() => setOpen(false)} className="lg:hidden"><X /></button></div>
      <nav className="mt-10 grid gap-1">{links.map(([to,label,Icon]) => <NavLink key={to} to={to} end={to === "/admin"} className={({ isActive }) => `flex items-center gap-3 rounded-2xl px-4 py-3 font-bold transition ${isActive ? "bg-cream text-ink" : "text-cream/65 hover:bg-white/10 hover:text-cream"}`}><Icon size={19} />{label}</NavLink>)}</nav>
      <div className="mt-auto border-t border-white/10 pt-5"><p className="truncate font-bold">{auth.user?.name}</p><p className="truncate text-xs text-cream/45">{auth.user?.email}</p><button onClick={() => auth.logout()} className="mt-4 flex items-center gap-2 text-sm font-bold text-cream/60 hover:text-cream"><LogOut size={17} /> Sign out</button></div>
    </aside>
    <main className="min-h-screen lg:pl-72"><div className="mx-auto max-w-[1500px] p-4 sm:p-7 lg:p-10"><Outlet /></div></main>
    <AdminHelpWidget />
  </div>;
}
